import React from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useStore } from 'react-redux';

import ErrorPage from './app/ErrorPage';
import VerifyLoginPage from './login/VerifyLoginPage';
import LoggedOutPage from './login/LoggedOutPage';
import ForbiddenPage from './login/ForbiddenPage';
import LogoutSuccessPage from './login/LogoutSuccessPage';
import { StatisticsPage } from './statistics/StatisticsPage';
import State from './app/store/state';
import VendorsPage from './vendors/VendorsPage';
import MobileDevicesPage from './mobile-devices/MobileDevicesPage';
import MobileDevicesUploadPage from './mobile-devices/MobileDevicesUploadPage';
import VendorEditPage from './vendors/VendorEditPage';
import VendorExportPage from './vendors/VendorExportPage';
import PaymentRequestsPage from './paymentRequests/PaymentRequestsPage';
import PaymentRequestDetailsPage from './paymentRequests/PaymentRequestDetailsPage';
import BeneficiaryExportPage from './beneficiaries/BeneficiaryExportPage';
import AsyncTaskDetailsView from './utils/AsyncTaskDetailsView';
import BeneficiariesPage from './beneficiaries/BeneficiariesPage';
import EditBeneficiaryPage from './beneficiaries/edit/EditBeneficiaryPage';
import TransactionsPage from './transactions/TransactionsPage';
import TransactionsExportPage from './transactions/TransactionsExportPage';
import SummaryStatisticsPage from './statistics/summaryStatistics/SummaryStatisticsPage';
import SecurityPage from './security/SecurityPage';
import ManagersMainPage from './managers/ManagersMainPage';
import ManagerEditPage from './managers/ManagerEditPage';
import ManagerEditPersonalPage from './managers/ManagerEditPersonalPage';
import DocumentsPage from './documents/DocumentsPage';
import TaskAuthorizationsPage, {
    adjustmentsRedirectPath,
    AuthorizationTab,
} from './authorization/TaskAuthorizationsPage';
import { hasFeatureAccess } from './login/auth';
import TransactionDetailsPage from './transactions/TransactionChainPage';
import EntitlementsView from './beneficiaries/EntitlementsView';
import BeneficiariesTokens from './beneficiaries/BeneficiariesTokens';
import BeneficiaryPendingActions from './beneficiaries/BeneficiaryPendingActions';
import { AdjustmentTransactions, ExpendituresTransactions } from './beneficiaries/BeneficiaryTransactions';
import BeneficiaryPersonalDetails from './beneficiaries/BeneficiaryPersonalDetails';
import BeneficiaryActivitiesPage from './beneficiaries/BeneficiaryActivitiesPage';
import TransactionAuthorizationPage from './authorization/TransactionAuthorizationPage';
import EntityUpdateAuthorizationPage from './authorization/EntityUpdateAuthorizationPage';
import BeneficiaryImportPage from './beneficiaries/fileUploadPages/BeneficiaryMassImport';
import AlternativeCollectorsImportPage from './beneficiaries/fileUploadPages/BeneficiaryAlternativeCollectorsImport';
import BeneficiaryMassUnblock from './beneficiaries/fileUploadPages/BeneficiaryMassUnblock';
import MassBlockWithoutZeroing from './beneficiaries/fileUploadPages/BeneficiaryMassBlock';
import BeneficiaryMassZeroing from './beneficiaries/fileUploadPages/BeneficiaryMassZeroing';
import BeneficiaryActivityLogs from './beneficiaries/BeneficiaryActivityLogs';
import ManagerLogExport from './activityLogs/MenagerActivityLogExport';
import BeneficiaryTotals from './beneficiaries/BeneficiaryTotals';
import BeneficiaryAlerts from './beneficiaries/BeneficiaryAlerts';
import BeneficiaryLogExport from './activityLogs/BeneficiaryActivityLogExport';
import OversightsPage from './oversights/OversightsPage';
import ActivityLogsDetailsView from './activityLogs/details/ActivityLogsDetailsView';
import ManagerExportPage from './managers/ManagerExportPage';
import ManagersPage from './managers/ManagersPage';
import ActivityLogView from './managers/ActivityLogView';
import OversightsExportPage from './oversights/OversightsExportPage';
import BeneficiaryRemoveAlternativeCollectors from './beneficiaries/fileUploadPages/BeneficiaryRemoveAlternativeCollectors';
import VendorUserList from './vendors/VendorUserList';
import VendorUserDetails from './vendors/VendorUserDetails';
import VendorPayment from './vendors/VendorPayment';
import ActivityLogsList from './activityLogs/ActivityLogsList';
import VendorDetails from './vendors/VendorDetails';
import AsyncTasksWithRetriesDetailsView from './utils/AsyncTasksWithRetriesDetailsView';
import PartnersPage from './partners/PartnersPage';
import PartnerEditPage from './partners/PartnerEditPage';
import PartnerDetails from './partners/PartnerDetails';
import PartnerAddPage from './partners/PartnerAddPage';
import { PartnerUsersList } from './partners/PartnerUsersList';
import { PartnerUserAddPage } from './partners/PartnerUserAddPage';
import { PartnerUserDetails } from './partners/PartnerUserDetails';
import LocationDetailsPage from './beneficiaries/locations/LocationDetailsPage';
import CreateLocationPage from './beneficiaries/locations/CreateLocationPage';
import LocationsPage from './beneficiaries/locations/LocationsPage';
import { MobilizationStatisticsPage } from './statistics/mobilizationStatistics/MobilizationStatisticsPage';
import { UploadDocumentsForm } from './documents/UploadDocumentsForm';
import { ProductsDetails } from './products/ProductsDetails';
import { ProductsVendors } from './products/ProductsVendors';
import { ProductsPageContainer } from './products/ProductsPageContainer';
import VendorBranches from './vendors/VendorBranches';
import VendorBranchesAdd from './vendors/VendorBranchesAdd';
import { VendorActivityLogExport } from './activityLogs/VendorActivityLogExport';
import PartnerActivityLogsList from './partners/PartnerActivityLogsList';
import { ProductUpdatePage } from './product-management/product-group-update/product-update.page';
import { EditOutletsPage } from './product-management/edit-outlets.page';
import { ProductManagementAuthorizationPage } from './authorization/product-management/product-management-authorization.container';
import { AddProductPage } from './product-management/AddProductPage';
import { VendorPermissionsFiles } from './vendors/VendorPermissionsFiles';
import { PermissionProfilesPage } from './permission-profiles/PermissionProfilesPage';
import { AddPermissionProfilePage } from './permission-profiles/AddPermissionProfilePage';
import PartnerPermissionsFiles from './partners/PartnerPermissionsFiles';
import { BeneficiaryLimitations } from './beneficiaries/BeneficiaryLimitations/BeneficiaryLimitations';
import { ManagerPermission, PRODUCTS_SECTION_PERMISSIONS } from './permission-profiles/permission';
import { BeneficiaryPinExportPage } from './beneficiaries/BeneficiaryPinExportPage';
import BeneficiaryMapping from './beneficiaries/fileUploadPages/BeneficiaryMapping';
import AppContainer from './app/AppContainer';
import HomeBasic from './app/HomeBasic';
import ProtectedLogin from './routing/ProtectedLogin';

const RedirectToRoot: React.FC = () => {
    const navigate = useNavigate();
    React.useEffect(() => {
        navigate('/', { replace: true });
    }, [navigate]);
    return null;
};

// Renders the home page based on the manager's permissions. Managers with
// summary-reports access keep the statistics dashboard; everyone else gets a
// neutral home page instead of being bounced off a feature-gated dashboard
// (which previously left them with failing requests and no reachable landing).
const HomeRoute: React.FC = () => {
    const manager = useSelector((s: State) => s.auth.manager);
    // Wait until the profile (permissions) is loaded before deciding what to show.
    if (!manager || !manager.profile?.permissions) {
        return null;
    }
    const canViewSummaryReports = manager.profile.permissions.includes(ManagerPermission.viewAndDownloadSummaryReports);
    return canViewSummaryReports ? <StatisticsPage /> : <HomeBasic />;
};

interface ProtectedRouteProps {
    checkFn?: () => string | void | undefined;
    children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ checkFn, children }) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [redirectTo, setRedirectTo] = React.useState<string | undefined | void>();
    const manager = useSelector((s: State) => s.auth.manager);
    const permissions = manager?.profile?.permissions;

    React.useEffect(() => {
        let isMounted = true;
        setIsLoading(true);

        const runCheck = async () => {
            try {
                const result = checkFn ? checkFn() : undefined;

                if (isMounted) {
                    setRedirectTo(result);
                    setIsLoading(false);
                }
            } catch (err) {
                if (isMounted) {
                    setRedirectTo('/unauthorized');
                    setIsLoading(false);
                }
            }
        };
        runCheck();

        return () => {
            isMounted = false;
        };
    }, [checkFn, permissions]);

    const waitingForPermissions = manager && !permissions;

    if (isLoading || waitingForPermissions) {
        return null;
    }
    if (redirectTo) {
        return <Navigate replace to={redirectTo} />;
    }
    return children ? <>{children}</> : <Outlet />;
};

const TransactionDetailsRoute: React.FC = () => {
    const params = useParams();
    return <TransactionDetailsPage params={{ transactionId: params.transactionId as string }} />;
};

const PaymentRequestDetailsRoute: React.FC = () => {
    const params = useParams();
    const location = useLocation();
    return <PaymentRequestDetailsPage location={location} params={{ requestId: params.requestId }} />;
};

const ManagerEditRoute: React.FC = () => {
    const params = useParams();
    return <ManagerEditPage params={{ managerId: params.managerId as string }} />;
};

const AsyncTaskDetailsRoute: React.FC = () => {
    const params = useParams();
    return <AsyncTaskDetailsView params={{ type: params.type as string, taskId: params.taskId as string }} />;
};

const ActivityLogsDetailsRoute: React.FC = () => {
    const params = useParams();
    return <ActivityLogsDetailsView params={{ logId: params.logId as string }} />;
};

const AppRoutes: React.FC = () => {
    const store = useStore<State>();
    const location = useLocation();

    const restrictedToFeatures =
        (features: ManagerPermission[], redirectPath = '/') =>
        () => {
            const auth = store.getState().auth;
            if (auth.manager) {
                // Wait for permissions to be loaded before checking
                if (!auth.manager.profile?.permissions) {
                    return undefined; // Don't redirect yet, permissions not loaded
                }
                let isAllowed = false;
                for (const feature of features) {
                    if (hasFeatureAccess(auth, feature)) {
                        isAllowed = true;
                        break;
                    }
                }
                if (!isAllowed) {
                    return redirectPath;
                }
            }
        };

    const restrictedToAllFeatures =
        (features: ManagerPermission[], redirectPath = '/') =>
        () => {
            const auth = store.getState().auth;
            if (auth.manager) {
                if (!auth.manager.profile?.permissions) {
                    return undefined;
                }
                for (const feature of features) {
                    if (!hasFeatureAccess(auth, feature)) {
                        return redirectPath;
                    }
                }
            }
        };

    const checkForBeneficiaryId = () => {
        // Allow access if beneficiaryId is in location.state
        // The component will handle null beneficiaryId gracefully
        if (location.state && (location.state as any).beneficiaryId) {
            return;
        }
        // Don't redirect - let the component handle the null case
        // This allows navigation from async task details and other sources
        return;
    };

    const checkForEntityId = (redirectPath: string) => () => {
        if (location.state && (location.state as any).entityId) {
            return;
        } else {
            return redirectPath;
        }
    };

    const checkForLocationFeatureToggle = (redirectPath: string) => () => {
        const featureToggle = store.getState().appConfig.location.isActive;

        if (featureToggle) {
            return;
        } else {
            return redirectPath;
        }
    };

    return (
        <Routes>
            <Route
                element={
                    <ProtectedLogin>
                        <AppContainer />
                    </ProtectedLogin>
                }
                path="/"
            >
                <Route element={<RedirectToRoot />} path={'login'} />
                <Route element={<VerifyLoginPage />} path="verify" />
                <Route element={<LoggedOutPage />} path="unauthorized" />
                <Route element={<ForbiddenPage />} path="forbidden" />
                <Route element={<LogoutSuccessPage />} path="logged-out" />
                <Route element={<Navigate replace to="/home" />} index />
                <Route element={<HomeRoute />} path="home" />

                {/* Vendors Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.vendorsRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="vendors"
                >
                    <Route element={<VendorsPage />} index />
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.vendorsDownload])}>
                                <VendorExportPage />
                            </ProtectedRoute>
                        }
                        path="download"
                    />
                    <Route element={<VendorEditPage />} path=":vendorId">
                        <Route element={<Navigate replace to="general" />} index />
                        <Route element={<VendorDetails />} path="general" />
                        <Route element={<VendorPayment />} path="payment" />
                        <Route element={<ActivityLogsList />} path="activity" />
                        <Route element={<VendorUserList />} path="panel" />
                        <Route element={<VendorUserDetails />} path="panel/add-user" />
                        <Route element={<VendorUserDetails />} path="panel/:userId" />
                        <Route element={<VendorBranches />} path="branches" />
                        <Route element={<VendorBranchesAdd />} path="branches/add" />
                        <Route element={<VendorActivityLogExport />} path="export-activity-log" />
                        <Route element={<VendorPermissionsFiles />} path="permission-files" />
                    </Route>
                </Route>

                {/* Mobile Devices Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.mobileDevicesRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="mobile-devices"
                >
                    <Route element={<MobileDevicesPage />} index />
                    <Route element={<MobileDevicesUploadPage />} path="import" />
                </Route>

                {/* Documents Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.managersManagement])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="documents"
                >
                    <Route element={<DocumentsPage />} index />
                </Route>
                <Route element={<UploadDocumentsForm />} path="documents/create" />

                {/* Partners Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.partnersRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="partners"
                >
                    <Route element={<PartnersPage />} index />
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.partnerPark])}>
                                <PartnerAddPage />
                            </ProtectedRoute>
                        }
                        path="new"
                    />
                    <Route element={<PartnerEditPage />} path=":partnerId">
                        <Route element={<Navigate replace to="general" />} index />
                        <Route element={<PartnerDetails />} path="general" />
                        <Route element={<PartnerActivityLogsList />} path="activity" />
                        <Route element={<PartnerUsersList />} path="panel" />
                        <Route element={<PartnerUserAddPage />} path="panel/add-user" />
                        <Route element={<PartnerUserDetails />} path="panel/:partnerUserId" />
                        <Route element={<PartnerPermissionsFiles />} path="permission-files" />
                    </Route>
                </Route>

                {/* Admin/Managers Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.managersManagement])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="admin"
                >
                    <Route element={<ManagersPage />} index />
                </Route>
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.userDownload])}>
                            <ManagerExportPage />
                        </ProtectedRoute>
                    }
                    path="admin/download"
                />
                <Route
                    element={
                        <ProtectedRoute
                            checkFn={restrictedToFeatures([
                                ManagerPermission.profilesRead,
                                ManagerPermission.profilesPark,
                            ])}
                        >
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="admin/permission-profiles"
                >
                    <Route element={<PermissionProfilesPage />} index />
                </Route>
                <Route
                    element={
                        <ProtectedRoute
                            checkFn={restrictedToFeatures([
                                ManagerPermission.profilesRead,
                                ManagerPermission.profilesPark,
                            ])}
                        >
                            <AddPermissionProfilePage />
                        </ProtectedRoute>
                    }
                    path="admin/permission-profiles/new"
                />
                <Route
                    element={
                        <ProtectedRoute
                            checkFn={restrictedToFeatures([
                                ManagerPermission.profilesRead,
                                ManagerPermission.profilesPark,
                            ])}
                        >
                            <PermissionProfilesPage />
                        </ProtectedRoute>
                    }
                    path="admin/permission-profiles/:id"
                />
                <Route path="admin/:managerId">
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.managersManagement])}>
                                <ManagerEditRoute />
                            </ProtectedRoute>
                        }
                        index
                    />
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.userActivityDownload])}>
                                <ManagerLogExport />
                            </ProtectedRoute>
                        }
                        path="export-activity-log"
                    />
                </Route>

                {/* Personal Routes */}
                <Route element={<ManagersMainPage />} path="personal">
                    <Route element={<Navigate replace to="details" />} index />
                    <Route element={<ManagerEditPersonalPage />} path="details" />
                    <Route element={<SecurityPage />} path="security" />
                    <Route element={<ActivityLogView />} path="activity-log" />
                </Route>

                {/* Beneficiaries Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.beneficiariesRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="beneficiaries"
                >
                    <Route element={<BeneficiariesPage />} index />
                    <Route element={<MobilizationStatisticsPage />} path="mobilization-statistics" />
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.beneficiariesDownload])}>
                                <BeneficiaryExportPage />
                            </ProtectedRoute>
                        }
                        path="download"
                    />
                    <Route element={<BeneficiaryMapping />} path="mapping" />
                    <Route
                        element={
                            <ProtectedRoute
                                checkFn={restrictedToFeatures([ManagerPermission.beneficiariesPinManagement])}
                            >
                                <BeneficiaryPinExportPage />
                            </ProtectedRoute>
                        }
                        path="export-pins"
                    />
                    <Route path="import">
                        <Route element={<BeneficiaryImportPage />} index />
                        <Route element={<AlternativeCollectorsImportPage />} path="alternative-collectors" />
                        <Route
                            element={<BeneficiaryRemoveAlternativeCollectors />}
                            path="remove-alternative-collectors"
                        />
                    </Route>

                    {/* Locations Routes */}
                    <Route
                        element={
                            <ProtectedRoute checkFn={checkForLocationFeatureToggle('/')}>
                                <Outlet />
                            </ProtectedRoute>
                        }
                        path="locations"
                    >
                        <Route element={<LocationsPage />} index />
                        <Route element={<CreateLocationPage />} path="new" />
                        <Route element={<LocationDetailsPage />} path=":locationId" />
                    </Route>

                    <Route element={<MassBlockWithoutZeroing />} path="block" />
                    <Route element={<BeneficiaryMassZeroing />} path="zeroing" />
                    <Route element={<BeneficiaryMassUnblock />} path="unblock" />
                    <Route
                        element={
                            <ProtectedRoute checkFn={checkForEntityId('/beneficiaries')}>
                                <ProtectedRoute
                                    checkFn={restrictedToFeatures([ManagerPermission.operationActivityDownload])}
                                >
                                    <BeneficiaryLogExport />
                                </ProtectedRoute>
                            </ProtectedRoute>
                        }
                        path="export-activity-log"
                    />
                    <Route
                        element={
                            <ProtectedRoute checkFn={checkForBeneficiaryId}>
                                <Outlet />
                            </ProtectedRoute>
                        }
                        path="beneficiary"
                    >
                        <Route element={<EditBeneficiaryPage />}>
                            <Route element={<Navigate replace to="main" />} index />
                            <Route element={<BeneficiaryActivitiesPage />} path="main" />
                            <Route element={<BeneficiaryPersonalDetails />} path="details" />
                            <Route element={<EntitlementsView />} path="entitlements" />
                            <Route element={<BeneficiariesTokens />} path="tokens" />
                            <Route element={<BeneficiaryPendingActions />} path="pending-actions" />
                            <Route
                                element={
                                    <AdjustmentTransactions
                                        location={{
                                            state: {
                                                beneficiaryId: '',
                                            },
                                        }}
                                    />
                                }
                                path="adjustments"
                            />
                            <Route element={<ExpendituresTransactions />} path="expenditures" />
                            <Route
                                element={
                                    <ProtectedRoute
                                        checkFn={restrictedToFeatures([ManagerPermission.productsLimitationRead])}
                                    >
                                        <BeneficiaryLimitations />
                                    </ProtectedRoute>
                                }
                                path="limitations"
                            />
                            <Route
                                element={
                                    <ProtectedRoute
                                        checkFn={restrictedToFeatures([ManagerPermission.transactionRequestRead])}
                                    >
                                        <BeneficiaryTotals />
                                    </ProtectedRoute>
                                }
                                path="totals"
                            />
                            <Route
                                element={
                                    <ProtectedRoute
                                        checkFn={restrictedToFeatures([ManagerPermission.transactionRequestRead])}
                                    >
                                        <BeneficiaryAlerts />
                                    </ProtectedRoute>
                                }
                                path="alerts"
                            />
                            <Route
                                element={
                                    <ProtectedRoute
                                        checkFn={restrictedToFeatures([ManagerPermission.beneficiariesRead])}
                                    >
                                        <BeneficiaryActivityLogs />
                                    </ProtectedRoute>
                                }
                                path="activity-logs"
                            />
                        </Route>
                    </Route>
                </Route>

                {/* Transactions Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.transactionRequestRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="transactions"
                >
                    <Route element={<TransactionsPage />} index />
                    <Route
                        element={
                            <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.transactionsDownload])}>
                                <TransactionsExportPage />
                            </ProtectedRoute>
                        }
                        path="download"
                    />
                    <Route element={<TransactionDetailsRoute />} path=":transactionId" />
                </Route>

                {/* Adjustments Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.adjustmentsView])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="adjustments"
                >
                    <Route element={<TaskAuthorizationsPage />}>
                        <Route element={<Navigate replace to={adjustmentsRedirectPath(store.getState())} />} index />
                        <Route
                            element={
                                <ProtectedRoute
                                    checkFn={restrictedToFeatures([ManagerPermission.transactionRequestRead])}
                                >
                                    <TransactionAuthorizationPage />
                                </ProtectedRoute>
                            }
                            path="transactions"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.beneficiariesRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.Beneficiaries} />
                                </ProtectedRoute>
                            }
                            path="beneficiaries"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.vendorsRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.VendorsDetails} />
                                </ProtectedRoute>
                            }
                            path="vendors"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.managersManagement])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.ManagerDetails} />
                                </ProtectedRoute>
                            }
                            path="users"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.vendorsRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.VendorsUsersDetails} />
                                </ProtectedRoute>
                            }
                            path="vendor-users"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.partnersRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.PartnersDetails} />
                                </ProtectedRoute>
                            }
                            path="partners"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.partnersRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.PartnersUsersDetails} />
                                </ProtectedRoute>
                            }
                            path="partner-users"
                        />
                        <Route
                            element={<EntityUpdateAuthorizationPage tab={AuthorizationTab.Locations} />}
                            path="locations"
                        />
                        <Route
                            element={
                                <ProtectedRoute
                                    checkFn={restrictedToFeatures([ManagerPermission.productGroupPricesView])}
                                >
                                    <ProductManagementAuthorizationPage />
                                </ProtectedRoute>
                            }
                            path="products"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.managersManagement])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.PermissionProfiles} />
                                </ProtectedRoute>
                            }
                            path="permission-profiles"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.mobileDevicesRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.MobileDevices} />
                                </ProtectedRoute>
                            }
                            path="mobile-devices"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.vendorsPost])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.BranchCodes} />
                                </ProtectedRoute>
                            }
                            path="branch-codes"
                        />
                        <Route
                            element={
                                <ProtectedRoute
                                    checkFn={restrictedToFeatures([ManagerPermission.productGroupUpdateVendorBranch])}
                                >
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.ProductPriceVersions} />
                                </ProtectedRoute>
                            }
                            path="product-price-versions"
                        />
                        <Route
                            element={
                                <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.productsRead])}>
                                    <EntityUpdateAuthorizationPage tab={AuthorizationTab.ProductsDetails} />
                                </ProtectedRoute>
                            }
                            path="product-details"
                        />
                    </Route>
                </Route>

                {/* Oversight Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.operationActivityView])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="oversight"
                >
                    <Route element={<OversightsPage />} index />
                    <Route
                        element={
                            <ProtectedRoute
                                checkFn={restrictedToFeatures([ManagerPermission.operationActivityDownload])}
                            >
                                <OversightsExportPage />
                            </ProtectedRoute>
                        }
                        path="download"
                    />
                </Route>

                {/* Tasks Routes */}
                <Route element={<AsyncTaskDetailsRoute />} path="tasks/:type/:taskId" />
                <Route element={<AsyncTasksWithRetriesDetailsView />} path="subtasks/:parentId/:subtaskCategory" />

                {/* Activity Logs Routes */}
                <Route
                    element={
                        <ProtectedRoute
                            checkFn={restrictedToFeatures([
                                ManagerPermission.operationActivityView,
                                ManagerPermission.userActivityView,
                            ])}
                        >
                            <ActivityLogsDetailsRoute />
                        </ProtectedRoute>
                    }
                    path="activity-logs/:logId"
                />

                {/* Summary Statistics Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.transactionRequestRead])}>
                            <SummaryStatisticsPage />
                        </ProtectedRoute>
                    }
                    path="summary-statistics"
                />

                {/* Payments Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures([ManagerPermission.paymentsRead])}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="payments"
                >
                    <Route element={<PaymentRequestsPage />} index />
                    <Route element={<PaymentRequestDetailsRoute />} path=":requestId" />
                </Route>

                {/* Products Routes */}
                <Route
                    element={
                        <ProtectedRoute checkFn={restrictedToFeatures(PRODUCTS_SECTION_PERMISSIONS, '/')}>
                            <Outlet />
                        </ProtectedRoute>
                    }
                    path="products"
                >
                    <Route element={<ProductsPageContainer />} index />
                    <Route element={<ProductsVendors />} path="vendors" />
                    <Route
                        element={
                            <ProtectedRoute
                                checkFn={restrictedToFeatures([ManagerPermission.productsCreateUpdate], '/products')}
                            >
                                <AddProductPage />
                            </ProtectedRoute>
                        }
                        path="add-product"
                    />
                    <Route
                        element={
                            <ProtectedRoute
                                checkFn={restrictedToFeatures(
                                    [ManagerPermission.productGroupUpdatePricesPark],
                                    '/products'
                                )}
                            >
                                <ProductUpdatePage />
                            </ProtectedRoute>
                        }
                        path="update/:groupName"
                    />
                    <Route
                        element={
                            <ProtectedRoute
                                checkFn={restrictedToAllFeatures(
                                    [
                                        ManagerPermission.productGroupUpdateVendorBranch,
                                        ManagerPermission.productGroupPricesView,
                                    ],
                                    '/products'
                                )}
                            >
                                <EditOutletsPage />
                            </ProtectedRoute>
                        }
                        path="update-vendors/:groupName"
                    />
                    <Route path=":productId">
                        <Route element={<Navigate replace to="general" />} index />
                        <Route element={<ProductsDetails />} path="general" />
                    </Route>
                </Route>
            </Route>

            <Route element={<ErrorPage />} path="*" />
        </Routes>
    );
};

export default AppRoutes;
